import { Flex, Tag, TagProps } from '@applyboard/crystal-ui'
import { ApplicationState } from '../../../hooks'
import { ApplicationStateInfo, isEnrichedState, getStateCode } from '../../../utils/applicationStateHelpers'

type ApplicationStateTagProps = {
  state?: ApplicationState | ApplicationStateInfo,
  hasOpenDocumentRequests?: boolean,
  vertical?: boolean
}

export default function ApplicationStateTag(props: ApplicationStateTagProps) {
  const getStateInfo = () => {
    const stateCode = getStateCode(props.state)
    
    if (isEnrichedState(props.state)) {
      const shouldDisplayDocumentRequestedTag = 
        props.hasOpenDocumentRequests &&
        (
          stateCode === ApplicationState.INITIAL_OFFER ||
          stateCode === ApplicationState.FINAL_OFFER_ISSUED ||
          stateCode === ApplicationState.WAITLISTED ||
          stateCode === ApplicationState.ENROLMENT_CONFIRMED
        )

      if (props.hasOpenDocumentRequests && !shouldDisplayDocumentRequestedTag) {
        return {
          intent: 'warning' as TagProps['intent'],
          label: 'Document Requested',
          shouldDisplayDocumentRequestedTag
        }
      }

      return {
        intent: props.state.intent as TagProps['intent'],
        label: props.state.label,
        shouldDisplayDocumentRequestedTag
      }
    }

    let intent = 'primary' as TagProps['intent']
    let label = 'Submitted'
    
    const shouldDisplayDocumentRequestedTag = 
      props.hasOpenDocumentRequests &&
      (
        stateCode === ApplicationState.INITIAL_OFFER ||
        stateCode === ApplicationState.FINAL_OFFER_ISSUED ||
        stateCode === ApplicationState.WAITLISTED ||
        stateCode === ApplicationState.ENROLMENT_CONFIRMED
      )
    
    if (props.hasOpenDocumentRequests && !shouldDisplayDocumentRequestedTag) {
      label = 'Document Requested'
      intent = 'warning'
    }

    switch (stateCode) {
      case ApplicationState.DECLINED:
        intent = 'negative'
        label = 'Declined'
        break
      case ApplicationState.PAYMENT_PROCESSING:
        intent = 'magic' as TagProps['intent']
        label = 'Payment Processing'
        break
      case ApplicationState.INITIAL_OFFER:
        intent = 'positive'
        label = 'Pre-Offer'
        break
      case ApplicationState.FINAL_OFFER_ISSUED:
        intent = 'positive'
        label = 'Final Offer'
        break
      case ApplicationState.WAITLISTED:
        intent = 'secondary'
        label = 'Waitlist'
        break
      case ApplicationState.DRAFT:
        intent = 'warning'
        label = 'Draft'
        break
      case ApplicationState.DEFERRAL_REQUESTED_IN_ASSESSMENT:
      case ApplicationState.DEFERRAL_REQUESTED_IN_SCREENING:
      case ApplicationState.DEFERRAL_REQUESTED_IN_INITIAL_OFFER:
        intent = 'warning'
        label = 'Deferral Requested'
        break
      case ApplicationState.ENROLMENT_CONFIRMED:
        intent = 'primary'
        label = 'Enrolled'
        break
    }

    return {
      intent,
      label,
      shouldDisplayDocumentRequestedTag
    }
  }

  const { intent, label, shouldDisplayDocumentRequestedTag } = getStateInfo()

  return (
    <Flex gap={2} align={'start'} direction={props.vertical ? 'column' : 'row'}>
      {shouldDisplayDocumentRequestedTag && <Tag icon="fill" intent="warning">Document Requested</Tag>}
      <Tag icon="fill" intent={intent}>
        {label}
      </Tag>
    </Flex>
  )
}
